import toast, { Toaster } from "react-hot-toast";
import React, { useEffect, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, useGLTF, Bounds } from "@react-three/drei";
import config from "../../services/config";
import AnatomyForm from "../anatomy/AnatomyForm";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import {
  rSetEnquiryId,
  rSetMuscleCode,
} from "../../redux/reducers/GlobalVariables";
import { hideLoader, showLoader } from "../../redux/reducers/loaderSlice";
import { useNavigate } from "react-router-dom";
import ScrollToTopMobile from "../../components/buttons/ScrollToTopMobile";
// Model Component
function Model({ onPartClick, groupCode, setGroupCode, setControlsEnabled }) {
  const { scene } = useGLTF("/model.glb");
  const [selectedMeshName, setSelectedMeshName] = useState(null);
  const [selectedMeshColor, setSelectedMeshColor] = useState(null);
  const lastTapRef = React.useRef(0);

  const defaultColors = {
    "05_Chest": "#FF6B6B",         // Soft red
    "06_Abdomen": "#FF6B6B",       // Light salmon
    "10_Upper_arms": "#FF6B6B",    // Golden yellow
    // "12_Fore_arms": "#FF6B6B",     // Sandy brown
    "15_Thighs": "#FF6B6B",        // Crimson
    // "17_Legs": "#FF6B6B",          // Dark orange
    "08_Intimate_area": "#FF6B6B",        // Royal blue
    "22_Buttocks": "#FF6B6B",      // Orchid (light purple)
    "07_Lower_abdomen": "#F80303",      // Orchid (light purple)

    // Add more with contrast if needed
  };
  
  
  

  useEffect(() => {
    let i = 0;
    const meshList = [];
    scene.traverse((child) => {
      if (child.isMesh) {
        console.log("Mesh Name:", child.name);
        meshList.push({ id: i, name: child.name });
        const meshName = child.name || "item" + i;
        child.userData.name = meshName;
        child.material = child.material.clone();
        const defaultColor = defaultColors[meshName] || "#FFDBAC";
        child.material.color.set(defaultColor);        
        i++;
      }
    });
    // console.log("All Meshes:", meshList);
  }, [scene]);

  useEffect(() => {
    if (!groupCode.length) return;
  
    scene.traverse((child) => {
      if (child.isMesh) {
        const matchedGroup = groupCode.find(
          (g) => g.group_code === child.userData.name
        );
  
        const highlightColor = matchedGroup?.highlight_color || "#128af6";
        const defaultColor = defaultColors[child.userData.name] || "#FFDBAC";
  
        child.material.color.set(
          child.userData.name === selectedMeshName
            ? highlightColor
            : defaultColor
        );
      }
    });
  }, [selectedMeshName, scene, groupCode]);
  

  useEffect(() => {
    const fetchGroupCode = async () => {
      try {
        const res = await config.getGroupFor3dModel({ company_id: 1 });
        if (res.status === 200) setGroupCode(res.data);
      } catch (error) {
        console.error("Error fetching group code:", error);
      }
    };

    fetchGroupCode();
  }, [setGroupCode]);

  return (
    <primitive
      object={scene}
      rotation={[0, 0, 0]}
      onPointerOver={() => setControlsEnabled(true)}
      onPointerOut={() => setControlsEnabled(false)}
      onPointerDown={(e) => {
        e.stopPropagation();
        const isTouch =
          "ontouchstart" in window || navigator.maxTouchPoints > 0;
        const partName = e.object?.userData?.name;

        if (!partName) return;

        if (isTouch) {
          const now = Date.now();
          if (now - lastTapRef.current < 300) {
            // Double-tap detected
            setSelectedMeshName(partName);
            onPartClick(partName);
          }
          lastTapRef.current = now;
        } else {
          // Desktop - single click is fine
          setSelectedMeshName(partName);
          onPartClick(partName);
        }
      }}
    />
  );
}

// ThreeDmodel Component
function ThreeDmodel() {
  const [groupCode, setGroupCode] = useState([]);
  const [open, setOpen] = useState(false);
  const [controlsEnabled, setControlsEnabled] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false); // 👈 Add this
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    // Detect touch device
    const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    setIsTouchDevice(isTouch);
  }, []);

  const handlePartClick = (partName) => {
    const group = groupCode.find((g) => g.group_code === partName);
    const hasGroup = !!group?.group_name;

    Swal.fire({
      title: hasGroup
        ? `Are you feeling pain in ${group.group_name}?`
        : `Click Part ${partName} 🚀 NEW FEATURE COMING SOON`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.isConfirmed && group) {
        setOpen(true);
        dispatch(rSetMuscleCode(group.id));
      }
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async (params) => {
    try {
      dispatch(showLoader());
      const res = await config.createEnquiry(params);
      if (res.status === 200) {
        const enquiry_id = res.enquiry_id;
        dispatch(rSetEnquiryId(enquiry_id));
      }
      navigate("/symptoms");
    } catch (e) {
      console.log(e);
    } finally {
      dispatch(hideLoader());
    }
  };

  useEffect(() => {
    dispatch(rSetMuscleCode(null));
    dispatch(rSetEnquiryId(null));
  }, []);

  return (
    <div style={{ width: "100vw", height: "100vh", position: "relative" }}>
      <Toaster position="top-center" />
      <Canvas
        camera={{ position: [0, 105, 105], fov: 45 }}
        style={{ background: "#1d2053" }}
      >
        {/* Lights */}
        <ambientLight intensity={0.6} />
        <directionalLight position={[10, 10, 10]} intensity={1} />
        <directionalLight position={[-10, 10, 10]} intensity={1} />
        <directionalLight position={[10, -10, -10]} intensity={1} />
        <directionalLight position={[0, 10, -10]} intensity={1} />

        <Bounds fit clip observe margin={1.2}>
          <Model
            onPartClick={handlePartClick}
            groupCode={groupCode}
            setGroupCode={setGroupCode}
            setControlsEnabled={setControlsEnabled}
          />
          <OrbitControls
            makeDefault
            enableZoom={controlsEnabled || isTouchDevice}
            //   enableRotate={controlsEnabled}
            target={[0, 1.5, 0]}
            enablePan={true}
            maxPolarAngle={Math.PI / 2} // optional: restrict rotation if needed
            minPolarAngle={0}
          />
        </Bounds>
      </Canvas>

      <AnatomyForm
        open={open}
        handleClose={handleClose}
        onSubmit={handleSubmit}
      />
      <ScrollToTopMobile />
    </div>
  );
}

export default ThreeDmodel;
